import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import StarIcon from '@mui/icons-material/Star';
import UndoIcon from '@mui/icons-material/Undo';
import {
  BarcodeStatus,
  CuratorCrop,
  CuratorStore,
  ForgeDesignCuratorPayload,
} from '../../types';
import { BarcodeStatusBadge, RequestVerificationButton } from '../../components/common/BarcodeVerification';
import { useNotification } from '../../hooks/useNotification';
import { actionBtnStyle } from '../../hooks/useUPCActions';

// ── Types ──

interface PendingChanges {
  addCropIds: string[];
  deactivateCropIds: string[];
  reassignments: { cropId: string; targetDesignId: string }[];
  markCarton: string[];
  markNonFront: string[];
  sendToAssignQueue: string[];
}

function emptyChanges(): PendingChanges {
  return {
    addCropIds: [],
    deactivateCropIds: [],
    reassignments: [],
    markCarton: [],
    markNonFront: [],
    sendToAssignQueue: [],
  };
}

interface StoreState extends CuratorStore {
  addedCropIds: Set<string>;
  deactivatedCropIds: Set<string>;
}

interface UndoEntry {
  label: string;
  stores: StoreState[];
  changes: PendingChanges;
}

interface DesignCuratorUIProps {
  payload: ForgeDesignCuratorPayload;
  onSelect: (data: PendingChanges) => void;
}

// ── Component ──

export const DesignCuratorUI: React.FC<DesignCuratorUIProps> = ({ payload, onSelect }) => {
  // Mock barcode status — will come from payload in production
  const [barcodeStatus, setBarcodeStatus] = useState<BarcodeStatus>('unconfirmed');

  const [stores, setStores] = useState<StoreState[]>(() =>
    payload.stores.map(s => ({
      ...s,
      addedCropIds: new Set<string>(),
      deactivatedCropIds: new Set<string>(),
    }))
  );
  const [changes, setChanges] = useState<PendingChanges>(emptyChanges);
  const [selectedCropIds, setSelectedCropIds] = useState<Set<string>>(new Set());
  const [collapsedStores, setCollapsedStores] = useState<Set<string>>(new Set());
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
  const [expandedSimilar, setExpandedSimilar] = useState<Set<string>>(new Set());
  const { notify, NotificationToast } = useNotification();

  // Save snapshot before mutations
  const pushUndo = useCallback((label: string) => {
    setUndoStack(prev => [...prev, {
      label,
      stores: stores.map(s => ({ ...s, addedCropIds: new Set(s.addedCropIds), deactivatedCropIds: new Set(s.deactivatedCropIds) })),
      changes: { ...changes, reassignments: [...changes.reassignments] },
    }]);
  }, [stores, changes]);

  const undo = useCallback(() => {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setStores(last.stores);
      setChanges(last.changes);
      setSelectedCropIds(new Set());
      notify(`Undid: ${last.label}`, 'warning');
      return prev.slice(0, -1);
    });
  }, [notify]);

  // Sync changes to parent
  useEffect(() => {
    onSelect(changes);
  }, [changes, onSelect]);

  // ── Computed ──

  const activeCropCount = useMemo(() => {
    let count = 0;
    for (const s of stores) {
      count += s.existingCrops.filter(c => !s.deactivatedCropIds.has(c.id)).length;
      count += s.candidateCrops.filter(c => s.addedCropIds.has(c.id)).length;
    }
    return count;
  }, [stores]);

  const perStoreCounts = useMemo(() => {
    return stores.map(s => {
      const existing = s.existingCrops.filter(c => !s.deactivatedCropIds.has(c.id)).length;
      const added = s.candidateCrops.filter(c => s.addedCropIds.has(c.id)).length;
      return { storeId: s.storeId, count: existing + added };
    }).filter(s => s.count > 0);
  }, [stores]);

  const uniformity = useMemo(() => {
    const counts = perStoreCounts.map(s => s.count);
    if (counts.length <= 1) return counts.length === 1 ? 100 : 0;
    const mean = counts.reduce((a, b) => a + b, 0) / counts.length;
    if (mean === 0) return 0;
    const variance = counts.reduce((a, c) => a + (c - mean) ** 2, 0) / counts.length;
    const cv = Math.sqrt(variance) / mean;
    return Math.round(Math.max(0, (1 - cv) * 100));
  }, [perStoreCounts]);

  const changeCount = useMemo(() => {
    return changes.addCropIds.length
      + changes.deactivateCropIds.length
      + changes.reassignments.length
      + changes.sendToAssignQueue.length;
  }, [changes]);

  // ── Selection ──

  // Track where selected crops come from
  const selectedCropType = useMemo((): 'existing' | 'candidate' | 'mixed' | null => {
    if (selectedCropIds.size === 0) return null;
    let hasExisting = false;
    let hasCandidate = false;
    for (const s of stores) {
      for (const c of s.existingCrops) {
        if (selectedCropIds.has(c.id)) hasExisting = true;
      }
      for (const c of s.candidateCrops) {
        if (selectedCropIds.has(c.id)) hasCandidate = true;
      }
    }
    if (hasExisting && hasCandidate) return 'mixed';
    if (hasExisting) return 'existing';
    if (hasCandidate) return 'candidate';
    return null;
  }, [selectedCropIds, stores]);

  const handleCropClick = useCallback((cropId: string) => {
    setSelectedCropIds(prev => {
      const next = new Set(prev);
      if (next.has(cropId)) next.delete(cropId);
      else next.add(cropId);
      return next;
    });
  }, []);

  // ── Actions ──

  const addCrops = useCallback(() => {
    const ids = [...selectedCropIds];
    pushUndo(`Add ${ids.length} crop(s)`);
    setStores(prev => prev.map(s => ({
      ...s,
      addedCropIds: new Set([...s.addedCropIds, ...ids.filter(id => s.candidateCrops.some(c => c.id === id))]),
    })));
    setChanges(prev => ({ ...prev, addCropIds: [...prev.addCropIds, ...ids] }));
    setSelectedCropIds(new Set());
    notify(`Added ${ids.length} crop${ids.length > 1 ? 's' : ''}`);
  }, [selectedCropIds, notify, pushUndo]);

  const deactivateCrops = useCallback(() => {
    const ids = [...selectedCropIds];
    pushUndo(`Deactivate ${ids.length} crop(s)`);
    setStores(prev => prev.map(s => ({
      ...s,
      deactivatedCropIds: new Set([...s.deactivatedCropIds, ...ids.filter(id => s.existingCrops.some(c => c.id === id))]),
    })));
    setChanges(prev => ({ ...prev, deactivateCropIds: [...prev.deactivateCropIds, ...ids] }));
    setSelectedCropIds(new Set());
    notify(`Deactivated ${ids.length} crop${ids.length > 1 ? 's' : ''}`, 'warning');
  }, [selectedCropIds, notify, pushUndo]);

  const reassignToDesign = useCallback((targetDesignId: string) => {
    const ids = [...selectedCropIds];
    pushUndo(`Reassign ${ids.length} crop(s)`);
    setStores(prev => prev.map(s => ({
      ...s,
      deactivatedCropIds: new Set([...s.deactivatedCropIds, ...ids.filter(id => s.existingCrops.some(c => c.id === id))]),
    })));
    setChanges(prev => ({
      ...prev,
      reassignments: [...prev.reassignments, ...ids.map(cropId => ({ cropId, targetDesignId }))],
    }));
    setSelectedCropIds(new Set());
    notify(`Reassigned ${ids.length} crop${ids.length > 1 ? 's' : ''} → ${targetDesignId.slice(0, 8)}…`);
  }, [selectedCropIds, notify, pushUndo]);

  const sendToQueue = useCallback(() => {
    const ids = [...selectedCropIds];
    pushUndo(`Send ${ids.length} to queue`);
    setStores(prev => prev.map(s => ({
      ...s,
      deactivatedCropIds: new Set([...s.deactivatedCropIds, ...ids.filter(id => s.existingCrops.some(c => c.id === id))]),
    })));
    setChanges(prev => ({ ...prev, sendToAssignQueue: [...prev.sendToAssignQueue, ...ids] }));
    setSelectedCropIds(new Set());
    notify(`Sent ${ids.length} crop${ids.length > 1 ? 's' : ''} to assign queue`);
  }, [selectedCropIds, notify, pushUndo]);

  // ESC to clear, Ctrl+Z to undo
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedCropIds(new Set());
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo]);

  // ── Render helpers ──

  const renderCrop = (crop: CuratorCrop, type: 'existing' | 'candidate', store: StoreState) => {
    const isSelected = selectedCropIds.has(crop.id);
    const isAdded = type === 'candidate' && store.addedCropIds.has(crop.id);
    const isDeactivated = type === 'existing' && store.deactivatedCropIds.has(crop.id);

    if (isDeactivated) return null;

    return (
      <div
        key={crop.id}
        onClick={() => !isAdded && handleCropClick(crop.id)}
        style={{
          position: 'relative',
          width: 80,
          height: 80,
          borderRadius: 'var(--radius-sm)',
          overflow: 'hidden',
          cursor: isAdded ? 'default' : 'pointer',
          border: isSelected
            ? '2px solid var(--blue)'
            : (isAdded || type === 'candidate')
              ? '2px dashed rgba(136,136,136,0.5)'
              : '2px solid var(--border)',
          boxShadow: isSelected ? '0 0 8px rgba(232,151,108,0.4)' : 'none',
          opacity: type === 'candidate' && !isAdded ? 0.75 : 1,
          transition: 'all 120ms ease',
          flexShrink: 0,
        }}
      >
        <img
          src={crop.imageUrl}
          alt={crop.id}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          draggable={false}
        />
        {/* Design color triangle (top-left corner) */}
        {crop.designColor && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 0,
            height: 0,
            borderTop: `16px solid ${crop.designColor}`,
            borderRight: '16px solid transparent',
            pointerEvents: 'none',
          }} />
        )}
        {/* Similarity badge */}
        <div style={{
          position: 'absolute',
          bottom: 2,
          right: 2,
          fontSize: 9,
          fontFamily: 'var(--font-mono)',
          fontWeight: 700,
          color: '#fff',
          background: crop.similarity >= 0.93 ? 'rgba(63,185,80,0.85)' : crop.similarity >= 0.85 ? 'rgba(210,153,34,0.85)' : 'rgba(248,81,73,0.85)',
          padding: '1px 4px',
          borderRadius: 3,
        }}>
          {(crop.similarity * 100).toFixed(0)}%
        </div>
        {isSelected && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(232,151,108,0.15)', pointerEvents: 'none' }} />
        )}
      </div>
    );
  };

  const renderStoreRow = (store: StoreState) => {
    const isCollapsed = collapsedStores.has(store.storeId);
    const existingVisible = store.existingCrops.filter(c => !store.deactivatedCropIds.has(c.id));
    const addedCandidates = store.candidateCrops.filter(c => store.addedCropIds.has(c.id));
    const candidatesNotAdded = store.candidateCrops.filter(c => !store.addedCropIds.has(c.id));
    const totalActive = existingVisible.length + addedCandidates.length;

    // Target per store for fill bar
    const activeStoreCount = perStoreCounts.length || 1;
    const targetPerStore = Math.ceil(payload.targetCropCount / Math.max(activeStoreCount, stores.length));
    const fillPct = Math.min(100, (totalActive / Math.max(targetPerStore, 1)) * 100);

    // Store health color: green if balanced, yellow if over-represented, red if way over
    const overRatio = totalActive / Math.max(targetPerStore, 1);
    const fillColor = overRatio > 2.5 ? 'var(--danger)' : overRatio > 1.5 ? 'var(--warning)' : fillPct >= 100 ? 'var(--accent)' : fillPct >= 50 ? 'var(--warning)' : 'var(--text-tertiary)';
    const countColor = overRatio > 2.5 ? 'var(--danger)' : overRatio > 1.5 ? 'var(--warning)' : 'var(--text-tertiary)';

    return (
      <div
        key={store.storeId}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
        }}
      >
        {/* Store header */}
        <div
          onClick={() => {
            setCollapsedStores(prev => {
              const next = new Set(prev);
              if (next.has(store.storeId)) next.delete(store.storeId);
              else next.add(store.storeId);
              return next;
            });
          }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
            cursor: 'pointer', background: 'var(--table-header-bg)',
            borderBottom: isCollapsed ? 'none' : '1px solid var(--border)', userSelect: 'none',
          }}
        >
          {isCollapsed
            ? <KeyboardArrowRightIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
            : <KeyboardArrowDownIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
          }
          <StorefrontOutlinedIcon style={{ fontSize: 14, color: existingVisible.length > 0 ? 'var(--accent)' : 'var(--text-tertiary)' }} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
            {store.storeName}
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-tertiary)' }}>
            {store.storeId}
          </span>
          <div style={{ flex: 1 }} />
          {/* Fill bar */}
          <div style={{ width: 60, height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{
              width: `${fillPct}%`, height: '100%', background: fillColor,
              borderRadius: 2, transition: 'width 200ms ease',
            }} />
          </div>
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 11, color: countColor,
            fontWeight: overRatio > 1.5 ? 700 : 400, minWidth: 24, textAlign: 'right',
          }}>
            {totalActive}
          </span>
        </div>

        {/* Store content — fixed split layout */}
        {!isCollapsed && (
          <div style={{ display: 'flex', padding: '10px 12px', minHeight: 56, gap: 0 }}>
            {/* Existing/Assigned crops */}
            <div style={{ flex: '0 0 55%', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{
                fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                color: 'var(--accent)', fontFamily: 'var(--font-mono)',
              }}>
                Assigned ({totalActive})
              </div>
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 6,
                alignContent: 'flex-start', minHeight: 40,
              }}>
                {totalActive > 0 ? (
                  <>
                    {existingVisible.map(c => renderCrop(c, 'existing', store))}
                    {addedCandidates.map(c => renderCrop(c, 'candidate', store))}
                  </>
                ) : (
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontStyle: 'italic', padding: '4px 0', alignSelf: 'center' }}>
                    No assigned crops
                  </span>
                )}
              </div>
            </div>

            {/* Fixed divider */}
            <div style={{
              width: 1, alignSelf: 'stretch', background: 'var(--divider)',
              margin: '0 12px', flexShrink: 0,
            }} />

            {/* Candidate crops */}
            <div style={{ flex: '0 0 calc(45% - 25px)', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{
                fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)',
              }}>
                Candidates ({candidatesNotAdded.length})
              </div>
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 6,
                alignContent: 'flex-start', minHeight: 40,
              }}>
                {candidatesNotAdded.length > 0 ? (
                  candidatesNotAdded.map(c => renderCrop(c, 'candidate', store))
                ) : (
                  <span style={{ fontSize: 10, color: 'var(--text-tertiary)', fontStyle: 'italic', padding: '4px 0', alignSelf: 'center' }}>
                    No candidates
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Main render ──

  const progressPct = Math.min(100, (activeCropCount / payload.targetCropCount) * 100);

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* ── Left sidebar ── */}
      <div style={{
        width: 300, flexShrink: 0, borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--surface)',
      }}>
        <div style={{ padding: 16, flex: 1, overflowY: 'auto' }}>
          {/* Representative */}
          <div style={{
            width: 80, height: 80, borderRadius: 'var(--radius)', overflow: 'hidden',
            border: '2px solid var(--accent)', marginBottom: 12,
          }}>
            <img src={payload.representativeImageUrl} alt="representative" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>

          {/* Design info */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)', fontWeight: 600, wordBreak: 'break-all', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{payload.designId}</span>
              <BarcodeStatusBadge status={barcodeStatus} />
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 2 }}>
              UPC: {payload.upc}
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-tertiary)' }}>
              Barcode: {payload.barcode}
            </div>
            <div style={{ marginTop: 8 }}>
              <RequestVerificationButton status={barcodeStatus} designId={payload.designId} onRequest={() => setBarcodeStatus('pending')} />
            </div>
          </div>

          {/* Progress */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>Crop count</span>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700,
                color: activeCropCount >= payload.targetCropCount ? 'var(--accent)' : 'var(--text-primary)',
              }}>
                {activeCropCount} / {payload.targetCropCount}
              </span>
            </div>
            <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                width: `${progressPct}%`, height: '100%',
                background: progressPct >= 100 ? 'var(--accent)' : 'var(--blue)',
                borderRadius: 3, transition: 'width 300ms ease',
              }} />
            </div>
          </div>

          {/* Uniformity */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>Uniformity</span>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700,
                color: uniformity >= 80 ? 'var(--accent)' : uniformity >= 50 ? 'var(--warning)' : 'var(--danger)',
              }}>
                {uniformity}%
              </span>
            </div>
            <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                width: `${uniformity}%`, height: '100%',
                background: uniformity >= 80 ? 'var(--accent)' : uniformity >= 50 ? 'var(--warning)' : 'var(--danger)',
                borderRadius: 3, transition: 'width 300ms ease',
              }} />
            </div>
            {/* Mini bar chart */}
            <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 32, marginTop: 8, padding: '0 2px' }}>
              {stores.map(s => {
                const existing = s.existingCrops.filter(c => !s.deactivatedCropIds.has(c.id)).length;
                const added = s.candidateCrops.filter(c => s.addedCropIds.has(c.id)).length;
                const total = existing + added;
                const maxCount = Math.max(...stores.map(st => {
                  const ex = st.existingCrops.filter(c => !st.deactivatedCropIds.has(c.id)).length;
                  const ad = st.candidateCrops.filter(c => st.addedCropIds.has(c.id)).length;
                  return ex + ad;
                }), 1);
                const barH = total > 0 ? Math.max(3, (total / maxCount) * 28) : 2;
                return (
                  <div key={s.storeId} title={`${s.storeName}: ${total} crops`} style={{
                    flex: 1, height: barH, background: total > 0 ? 'var(--blue)' : 'var(--border)',
                    borderRadius: 1, transition: 'height 200ms ease', minWidth: 4,
                  }} />
                );
              })}
            </div>
          </div>

          {/* Stores summary */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Stores</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-tertiary)' }}>
              {perStoreCounts.length} with crops · {stores.length} total
            </div>
          </div>

          {/* Changes + Undo */}
          {changeCount > 0 && (
            <div style={{
              padding: '8px 10px', background: 'var(--accent-subtle)', border: '1px solid var(--accent-border)',
              borderRadius: 'var(--radius-sm)', marginBottom: 12,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--accent)' }}>
                  {changeCount} pending change{changeCount > 1 ? 's' : ''}
                </div>
                {undoStack.length > 0 && (
                  <button onClick={undo} title="Undo (Ctrl+Z)" style={{
                    background: 'none', border: '1px solid var(--accent-border)', borderRadius: 'var(--radius-sm)',
                    padding: '1px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
                    color: 'var(--accent)', fontSize: 10, fontFamily: 'var(--font-mono)',
                  }}>
                    <UndoIcon style={{ fontSize: 11 }} />
                    Undo
                  </button>
                )}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>Submit to apply</div>
            </div>
          )}

          <div style={{ height: 1, background: 'var(--divider)', margin: '4px 0 12px' }} />

          {/* Similar designs */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
              Similar Designs
            </div>
            {payload.similarDesigns.map(sv => {
              const isReassignTarget = selectedCropIds.size > 0
                && selectedCropType === 'existing';
              const isExpanded = expandedSimilar.has(sv.id);

              return (
                <div key={sv.id} style={{
                  marginBottom: 8, borderRadius: 'var(--radius-sm)',
                  border: isReassignTarget ? '1px dashed var(--blue)' : '1px solid var(--border)',
                  background: isReassignTarget ? 'rgba(232,151,108,0.06)' : 'var(--elevated)',
                  overflow: 'hidden', transition: 'all 120ms ease',
                }}>
                  {/* Header */}
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                      cursor: isReassignTarget ? 'pointer' : 'default',
                    }}
                    onClick={() => {
                      if (isReassignTarget) {
                        reassignToDesign(sv.id);
                      }
                    }}
                    onMouseEnter={e => { if (isReassignTarget) e.currentTarget.style.background = 'rgba(232,151,108,0.12)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ width: 48, height: 48, borderRadius: 'var(--radius-sm)', overflow: 'hidden', border: '1px solid var(--border)', flexShrink: 0 }}>
                      <img src={sv.representativeImageUrl} alt={sv.id} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{
                        fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600,
                        color: 'var(--text-primary)',
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {sv.id.slice(0, 12)}…
                        </span>
                        <BarcodeStatusBadge status={sv.barcodeStatus} />
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', marginTop: 1 }}>
                        {(sv.similarity * 100).toFixed(0)}% similar · {sv.cropCount} crops
                      </div>
                      {sv.barcode && (
                        <div style={{ fontSize: 9, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', marginTop: 1 }}>
                          {sv.barcode}
                        </div>
                      )}
                    </div>
                    {isReassignTarget && (
                      <StarIcon style={{ fontSize: 14, color: 'var(--blue)', flexShrink: 0 }} />
                    )}
                  </div>

                  {/* Expand toggle */}
                  {sv.sampleCrops.length > 0 && (
                    <div
                      onClick={() => setExpandedSimilar(prev => {
                        const next = new Set(prev);
                        if (next.has(sv.id)) next.delete(sv.id); else next.add(sv.id);
                        return next;
                      })}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px',
                        borderTop: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none',
                      }}
                    >
                      {isExpanded
                        ? <KeyboardArrowDownIcon style={{ fontSize: 14, color: 'var(--text-tertiary)' }} />
                        : <KeyboardArrowRightIcon style={{ fontSize: 14, color: 'var(--text-tertiary)' }} />
                      }
                      <span style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {sv.sampleCrops.length} samples
                      </span>
                    </div>
                  )}

                  {/* Sample crops grid */}
                  {isExpanded && (
                    <div style={{
                      display: 'flex', flexWrap: 'wrap', gap: 4, padding: '6px 10px 8px',
                      borderTop: '1px solid var(--border)',
                    }}>
                      {sv.sampleCrops.map(sc => (
                        <div key={sc.id} style={{
                          width: 56, height: 56, borderRadius: 'var(--radius-sm)', overflow: 'hidden',
                          border: '1px solid var(--border)', flexShrink: 0,
                        }}>
                          <img src={sc.imageUrl} alt={sc.id} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} draggable={false} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {selectedCropIds.size > 0 && selectedCropType === 'existing' && (
              <div style={{ fontSize: 10, color: 'var(--blue)', fontStyle: 'italic', marginTop: 6, padding: '0 8px' }}>
                Click a design to reassign {selectedCropIds.size} crop{selectedCropIds.size > 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Main area ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Action bar — when crops selected */}
        {selectedCropIds.size > 0 && (
          <div style={{
            padding: '8px 16px', background: 'var(--accent-subtle)', borderBottom: '1px solid var(--accent-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--blue)' }}>
                {selectedCropIds.size} crop{selectedCropIds.size > 1 ? 's' : ''} selected
              </span>

              {selectedCropIds.size === 1 && (() => {
                const cropId = [...selectedCropIds][0];
                let crop: CuratorCrop | undefined;
                for (const s of stores) {
                  crop = s.existingCrops.find(c => c.id === cropId) || s.candidateCrops.find(c => c.id === cropId);
                  if (crop) break;
                }
                return crop ? (
                  <span style={{ fontSize: 10, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', display: 'flex', gap: 8 }}>
                    <span>{crop.storeId}</span>
                    <span>{crop.source}</span>
                    <span>{(crop.similarity * 100).toFixed(0)}%</span>
                  </span>
                ) : null;
              })()}

              <span style={{ color: 'var(--border)', fontSize: 11 }}>|</span>

              {(selectedCropType === 'candidate' || selectedCropType === 'mixed') && (
                <button onClick={addCrops} style={actionBtnStyle('var(--accent)')}>
                  <AddCircleOutlineIcon style={{ fontSize: 13 }} />
                  Add
                </button>
              )}

              {(selectedCropType === 'existing' || selectedCropType === 'mixed') && (
                <button onClick={deactivateCrops} style={actionBtnStyle('var(--warning)')}>
                  <VisibilityOffOutlinedIcon style={{ fontSize: 13 }} />
                  Deactivate
                </button>
              )}

              {selectedCropType === 'existing' && (
                <button onClick={sendToQueue} style={actionBtnStyle('var(--purple)')}>
                  <SendOutlinedIcon style={{ fontSize: 13 }} />
                  To assign queue
                </button>
              )}

            </div>
            <button
              onClick={() => setSelectedCropIds(new Set())}
              style={{
                background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                color: 'var(--text-secondary)', fontSize: 11, padding: '2px 10px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
              }}
            >
              ESC
            </button>
          </div>
        )}

        {/* Store rows */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {stores.filter(s => s.existingCrops.length > 0).map(renderStoreRow)}

          {stores.some(s => s.existingCrops.length > 0) && stores.some(s => s.existingCrops.length === 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
              <div style={{ flex: 1, height: 1, background: 'var(--divider)' }} />
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                New stores
              </span>
              <div style={{ flex: 1, height: 1, background: 'var(--divider)' }} />
            </div>
          )}

          {stores.filter(s => s.existingCrops.length === 0).map(renderStoreRow)}
        </div>

        {NotificationToast}
      </div>
    </div>
  );
};

